import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { selectApi } from '@/modules/calendar/api';
import { NewsEvent } from '@/modules/calendar/model/calendar/news-event';
import { DataRelease } from '@/modules/calendar/model/calendar/data-release';
import { ReportingInterval } from '@/modules/calendar/model/calendar/reporting-interval';
import { useEffectivePermissions, Permissions } from '@/core/permissions/export';

const EXPAND_HISTORY_MONTHS = 6;

// Mirrors af-select-service's CalendarServiceFilteringDecorator
const DEFAULT_HISTORY_PERMISSION_DAYS = 365;
const HISTORY_PERMISSION_PATTERN = /read:select:(\d+)([dmy])/;

// The highest historical-access tier (`read:select:20y`).
const MAX_HISTORY_YEARS = 20;
export const MIN_HISTORY_START = dayjs().subtract(MAX_HISTORY_YEARS, 'year').valueOf();

function getPermittedHistoryStart(permissions: string[]): number {
  const match = permissions.map(p => p.match(HISTORY_PERMISSION_PATTERN)).find(Boolean);

  if (!match) return dayjs().subtract(DEFAULT_HISTORY_PERMISSION_DAYS, 'day').valueOf();

  const unit = match[2] === 'd' ? 'day' : match[2] === 'm' ? 'month' : 'year';

  return dayjs().subtract(parseInt(match[1], 10), unit).valueOf();
}

function getIntervalStart(interval: ReportingInterval): number {
  switch (interval) {
    case 'WEEKLY':
      return dayjs().subtract(3, 'month').valueOf();
    case 'QUARTERLY':
      return dayjs().subtract(24, 'month').valueOf();
    case 'EVERY_6_WEEKS':
      return dayjs().subtract(16, 'month').valueOf();
    default:
      return dayjs().subtract(12, 'month').valueOf();
  }
}

export default function useDataReleaseHistory(
  dataReleaseId: number | string,
  date?: string,
  fullHistory = false,
) {
  const [events, setEvents] = useState<NewsEvent[]>([]);
  const [selectedRelease, setSelectedRelease] = useState<DataRelease>();
  const [interval, setInterval] = useState<ReportingInterval>();
  const [historyStart, setHistoryStart] = useState<number>();
  const [expanding, setExpanding] = useState(false);
  const [historyExhausted, setHistoryExhausted] = useState(false);
  const permissions = useEffectivePermissions();

  useEffect(() => {
    selectApi.getAllReleases().then(value => {
      const dataRelease = value.find(v => `${v.id}` === `${dataReleaseId}`);

      if (!dataRelease) return;

      setSelectedRelease(dataRelease);
      setInterval(dataRelease.series[0].interval || 'MONTHLY');
    });
  }, [dataReleaseId]);

  useEffect(() => {
    if (!interval) return;

    const start = getIntervalStart(interval);
    const end = fullHistory ? dayjs().valueOf() : dayjs(date).valueOf() + 30_000;

    setHistoryStart(start);
    setHistoryExhausted(false);
    selectApi.getCalendarForDataRelease(dataReleaseId, start, end).then(setEvents);
  }, [dataReleaseId, interval, date, fullHistory]);

  const hasExpandHistoryAccess = permissions.includes(Permissions.ALPHA_ACCESS_DATA);
  const permittedHistoryStart = getPermittedHistoryStart(permissions);
  const permissionLimited =
    historyStart !== undefined &&
    historyStart > MIN_HISTORY_START &&
    permittedHistoryStart > MIN_HISTORY_START &&
    historyStart <= permittedHistoryStart;

  function expandHistory() {
    if (expanding || historyExhausted || permissionLimited) return;
    if (historyStart === undefined || historyStart <= MIN_HISTORY_START) return;

    const newStart = Math.max(
      MIN_HISTORY_START,
      dayjs(historyStart).subtract(EXPAND_HISTORY_MONTHS, 'month').valueOf(),
    );

    setExpanding(true);
    selectApi
      .getCalendarForDataRelease(dataReleaseId, newStart, historyStart - 1)
      .then(olderEvents => {
        if (olderEvents.length === 0) setHistoryExhausted(true);
        setEvents(current => [...olderEvents, ...current]);
      })
      .finally(() => setExpanding(false));

    setHistoryStart(newStart);
  }

  return {
    events,
    selectedRelease,
    canExpandHistory:
      hasExpandHistoryAccess &&
      !expanding &&
      !historyExhausted &&
      !permissionLimited &&
      historyStart !== undefined &&
      historyStart > MIN_HISTORY_START,
    expandHistory: hasExpandHistoryAccess ? expandHistory : undefined,
    permissionLimited,
  };
}
